import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import BaseLayout from './BaseLayout';
import '../styles/EditProgram.css';
import '../styles/performance-logger.css';
import '../styles/train.css';
import VideoModal from './ExerciseVideoModal';
import { buildApiUrl } from '../config/api';

const EditProgram = () => {
    const { programId } = useParams();
    const navigate = useNavigate();
    const { token } = useAuth();
    
    // Program and Session State
    const [program, setProgram] = useState(null);
    const [currentSession, setCurrentSession] = useState(null);
    
    // Add isInitializing state
    const [isInitializing, setIsInitializing] = useState(false);
    
    // Exercise Selection State
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState('');
    const [filteredExercises, setFilteredExercises] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedExercise, setSelectedExercise] = useState(null);
    const [showExerciseForm, setShowExerciseForm] = useState(false);

    // Exercise Entry Form State
    const [exerciseEntry, setExerciseEntry] = useState({
        exerciseName: '',
        series: 'A1',
        sets: 3,
        reps: 10,
        tempo: '4-0-1-0',
        rest: 60,
        notes: ''
    });

    // Add state for tracking the exercise being deleted
    const [deletingExercise, setDeletingExercise] = useState(null);

    // Add state for expanded session
    const [expandedSession, setExpandedSession] = useState(null);

    // Add state for selected video
    const [selectedVideo, setSelectedVideo] = useState(null);

    // Add function to initialize sessions
    const initializeSessions = async () => {
        setIsInitializing(true);
        try {
            const response = await axios.post(
                buildApiUrl(`/programs/${programId}/sessions/initialize`),
                {
                    numberOfSessions: 1
                },
                {
                    headers: { 'Authorization': `Bearer ${token}` }
                }
            );
            await fetchProgram();
        } catch (error) {
            console.error('Error initializing sessions:', error);
        } finally {
            setIsInitializing(false);
        }
    };

    // Fetch program data
    const fetchProgram = async () => {
        try {
            const response = await axios.get(
                buildApiUrl(`/programs/${programId}`),
                {
                    headers: { 'Authorization': `Bearer ${token}` }
                }
            );
            setProgram(response.data);
            // Ensure current session is updated with the latest data
            if (currentSession) {
                const updatedSession = response.data.sessions.find(s => s.id === currentSession.id);
                setCurrentSession(updatedSession);
            }
        } catch (error) {
            console.error('Error fetching program:', error);
        }
    };

    // Initial data fetch
    useEffect(() => {
        if (programId) {
            fetchProgram();
            fetchCategories();
        }
    }, [programId, token]);

    // Fetch exercise categories
    const fetchCategories = async () => {
        try {
            const response = await axios.get(
                buildApiUrl('/exercises/category'),
                {
                    headers: { 'Authorization': `Bearer ${token}` }
                }
            );
            setCategories(response.data || []);
        } catch (error) {
            console.error('Error fetching categories:', error);
            setCategories([]);
        }
    };

    // Handle category selection and fetch exercises
    const handleCategoryChange = async (e) => {
        const category = e.target.value;
        setSelectedCategory(category);
        
        if (category) {
            try {
                const response = await axios.get(
                    buildApiUrl(`/exercises?category=${encodeURIComponent(category)}`),
                    {
                        headers: { 'Authorization': `Bearer ${token}` }
                    }
                );
                setFilteredExercises(response.data);
            } catch (error) {
                console.error('Error fetching exercises:', error);
                setFilteredExercises([]);
            }
        } else {
            setFilteredExercises([]);
        }
    };

    // Handle exercise selection
    const handleExerciseSelect = (exercise) => {
        setSelectedExercise(exercise);
        setExerciseEntry(prev => ({
            ...prev,
            exerciseName: exercise.name
        }));
    };

    // Handle form input changes
    const handleEntryChange = (e) => {
        const { name, value } = e.target;
        setExerciseEntry(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Ensure currentSession is correctly set before making the request
    const handleExerciseSubmit = async () => {
        if (!currentSession || !selectedExercise) return;

        try {
            const response = await axios.post(
                buildApiUrl(`/programs/${programId}/sessions/${currentSession.id}/exercises`),
                {
                    exerciseName: selectedExercise.name,
                    series: exerciseEntry.series,
                    sets: parseInt(exerciseEntry.sets),
                    reps: parseInt(exerciseEntry.reps),
                    tempo: exerciseEntry.tempo,
                    rest: parseInt(exerciseEntry.rest),
                    notes: exerciseEntry.notes
                },
                {
                    headers: { 'Authorization': `Bearer ${token}` }
                }
            );

            // Assuming the response contains the new exercise entry with its ID
            const newExerciseEntry = response.data;

            // Update the state with the new exercise entry
            setProgram(prevProgram => {
                const updatedSessions = prevProgram.sessions.map(session => {
                    if (session.id === currentSession.id) {
                        return {
                            ...session,
                            exercises: [...session.exercises, newExerciseEntry]
                        };
                    }
                    return session;
                });
                return { ...prevProgram, sessions: updatedSessions };
            });

            setShowExerciseForm(false);
            setSelectedExercise(null);
            resetExerciseEntry();
        } catch (error) {
            console.error('Error adding exercise:', error);
        }
    };

    // Reset exercise entry form
    const resetExerciseEntry = () => {
        setExerciseEntry({
            exerciseName: '',
            series: 'A1',
            sets: 3,
            reps: 10,
            tempo: '4-0-1-0',
            rest: 60,
            notes: ''
        });
    };

    // Add search functionality
    const handleSearchChange = (e) => {
        const searchValue = e.target.value;
        setSearchTerm(searchValue);

        if (searchValue.length >= 2) {
            const searchExercises = async () => {
                try {
                    const response = await axios.get(
                        buildApiUrl(`/exercises/search?q=${encodeURIComponent(searchValue)}`),
                        {
                            headers: { 'Authorization': `Bearer ${token}` }
                        }
                    );
                    setFilteredExercises(response.data);
                } catch (error) {
                    console.error('Error searching exercises:', error);
                }
            };
            searchExercises();
        }
    };

    // Update the handleDeleteExercise function
    const handleDeleteExercise = async (exercise, sessionId) => {
        try {
            setDeletingExercise(exercise.id);
            await axios.delete(
                buildApiUrl(`/programs/${programId}/sessions/${sessionId}/exercises/${exercise.id}`),
                {
                    headers: { 'Authorization': `Bearer ${token}` }
                }
            );

            // Optimistically update the UI
            const updatedSessions = program.sessions.map(session => {
                if (session.id === sessionId) {
                    return {
                        ...session,
                        exercises: session.exercises.filter(e => e.id !== exercise.id)
                    };
                }
                return session;
            });
            setProgram({ ...program, sessions: updatedSessions });

            // Fetch fresh data in the background
            await fetchProgram();
        } catch (error) {
            console.error('Error deleting exercise:', error);
        } finally {
            setDeletingExercise(null);
        }
    };

    // Add this function to handle session selection
    const handleSessionSelect = (sessionId) => {
        setExpandedSession(expandedSession === sessionId ? null : sessionId);
    };

    const handleAddExerciseClick = (session) => {
        setCurrentSession(session);
        setShowExerciseForm(true);
    };

    const handleExerciseFormSubmit = (newExercise) => {
        // Logic to add the new exercise to the session
        setProgram(prevProgram => {
            const updatedSessions = prevProgram.sessions.map(session => {
                if (session.id === currentSession.id) {
                    return {
                        ...session,
                        exercises: [...session.exercises, newExercise]
                    };
                }
                return session;
            });
            return { ...prevProgram, sessions: updatedSessions };
        });
        setShowExerciseForm(false);
    };

    const handleDemoClick = (youtubeLink) => {
        setSelectedVideo(youtubeLink);
    };

    // Render session tiles with exercise management
    const renderSessionTiles = () => (
        <div className="session-tiles-container">
            <h3>Training Sessions</h3>
            <div className="session-tiles">
                {program.sessions.map((session, index) => (
                    <div key={session.id} className="session-tile">
                        <div className="session-tile-header">
                            <h4>Session {index + 1}</h4>
                            <span className="session-name">{session.name}</span>
                        </div>
                        <div className="session-tile-content">
                            <div className="exercise-summary">
                                {session.exercises && session.exercises.length > 0 ? (
                                    <div className="exercise-list-preview">
                                        {session.exercises.map((exercise) => (
                                            <div key={exercise.id} className="exercise-preview-item">
                                                <div className="exercise-info">
                                                    <span className="exercise-name">{exercise.exerciseName}</span>
                                                    <span className="exercise-details">
                                                        {exercise.sets}×{exercise.reps} ({exercise.series})
                                                    </span>
                                                </div>
                                                <button
                                                    className={`delete-exercise-btn ${deletingExercise === exercise.id ? 'deleting' : ''}`}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        const confirmDelete = window.confirm(
                                                            `Are you sure you want to delete "${exercise.exerciseName}"?`
                                                        );
                                                        if (confirmDelete) {
                                                            handleDeleteExercise(exercise, session.id);
                                                        }
                                                    }}
                                                    disabled={deletingExercise === exercise.id}
                                                >
                                                    {deletingExercise === exercise.id ? '...' : '×'}
                                                </button>
                                                {exercise.youtubeLink && (
                                                    <button 
                                                        className="demo-button"
                                                        onClick={() => handleDemoClick(exercise.youtubeLink)}
                                                    >
                                                        Demo
                                                    </button>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p>No exercises added to this session yet.</p>
                                )}
                            </div>
                            <div className="session-actions">
                                <button 
                                    className="add-exercise-btn"
                                    onClick={() => handleAddExerciseClick(session)}
                                >
                                    Add Exercise
                                </button>
                                <Link 
                                    to={`/programs/${program.id}/sessions/${session.id}/train`}
                                    className="start-training-btn"
                                >
                                    Start Training
                                </Link>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Exercise Form Modal */}
            {showExerciseForm && currentSession && (
                <div className="exercise-form-overlay">
                    <div className="exercise-form">
                        <h3>Add Exercise to Session {currentSession.name}</h3>
                        
                        {/* Exercise Search Section */}
                        <div className="form-section">
                            <input
                                type="text"
                                placeholder="Search exercises..."
                                value={searchTerm}
                                onChange={handleSearchChange}
                                className="search-input"
                            />
                            
                            <select
                                value={selectedCategory}
                                onChange={handleCategoryChange}
                                className="category-select"
                            >
                                <option value="">All Categories</option>
                                {categories.map(category => (
                                    <option key={category} value={category}>
                                        {category}
                                    </option>
                                ))}
                            </select>

                            <div className="exercise-grid">
                                {filteredExercises.map(exercise => (
                                    <div
                                        key={exercise.id}
                                        className={`exercise-card ${selectedExercise?.id === exercise.id ? 'selected' : ''}`}
                                        onClick={() => handleExerciseSelect(exercise)}
                                    >
                                        <div>{exercise.name}</div>
                                        <span className="exercise-category">{exercise.category}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Exercise Parameters Form */}
                        {selectedExercise && (
                            <div className="exercise-details-form">
                                <h4>Configure Exercise Parameters</h4>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label>Series</label>
                                        <input
                                            type="text"
                                            name="series"
                                            value={exerciseEntry.series}
                                            onChange={handleEntryChange}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Sets</label>
                                        <input
                                            type="number"
                                            name="sets"
                                            value={exerciseEntry.sets}
                                            onChange={handleEntryChange}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Reps</label>
                                        <input
                                            type="number"
                                            name="reps"
                                            value={exerciseEntry.reps}
                                            onChange={handleEntryChange}
                                        />
                                    </div>
                                </div>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label>Tempo</label>
                                        <input
                                            type="text"
                                            name="tempo"
                                            value={exerciseEntry.tempo}
                                            onChange={handleEntryChange}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Rest (seconds)</label>
                                        <input
                                            type="number"
                                            name="rest"
                                            value={exerciseEntry.rest}
                                            onChange={handleEntryChange}
                                        />
                                    </div>
                                </div>
                                <div className="form-group">
                                    <label>Notes</label>
                                    <textarea
                                        name="notes"
                                        value={exerciseEntry.notes}
                                        onChange={handleEntryChange}
                                    />
                                </div>
                            </div>
                        )}

                        <div className="form-actions">
                            <button 
                                className="submit-btn"
                                onClick={handleExerciseSubmit}
                                disabled={!selectedExercise}
                            >
                                Save Exercise
                            </button>
                            <button 
                                className="cancel-btn"
                                onClick={() => {
                                    setShowExerciseForm(false);
                                    setSelectedExercise(null);
                                    resetExerciseEntry();
                                }}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );

    return (
        <BaseLayout>
            <div className="edit-program-container">
                {program ? (
                    <>
                        <div className="program-header">
                            <h2>{program.name}</h2>
                            <p>{program.description}</p>
                        </div>
                        {program.sessions?.length === 0 ? (
                            <div className="no-sessions">
                                <p>This program has no sessions.</p>
                                <button 
                                    className="initialize-btn"
                                    onClick={initializeSessions}
                                    disabled={isInitializing}
                                >
                                    {isInitializing ? 'Initializing...' : 'Initialize Sessions'}
                                </button>
                            </div>
                        ) : (
                            renderSessionTiles()
                        )}
                    </>
                ) : (
                    <div className="loading">Loading program...</div>
                )}
            </div>
            {selectedVideo && (
                <VideoModal 
                    videoUrl={selectedVideo} 
                    onClose={() => setSelectedVideo(null)} 
                />
            )}
        </BaseLayout>
    );
};

export default EditProgram; 